import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import moment from 'moment-timezone';
import { RRule } from 'rrule';
import {
  Button,
  Calendar,
  CalendarProps,
  Modal,
  Skeleton,
  Typography,
  message,
} from '@robinpowered/ui-kit';
import ChevronLeftOutline from '@robinpowered/ui-kit-icons/ChevronLeftOutline';
import ChevronRightOutline from '@robinpowered/ui-kit-icons/ChevronRightOutline';
import momentGenerateConfig from 'rc-picker/es/generate/moment';
import { useTranslation } from 'react-i18next';
import {
  GetProfileDeskExclusionsQuery,
  useGetProfileDeskExclusionsQuery,
  usePatchProfileDeskExclusionsMutation,
} from 'generated/graphql';
import type { ProfileReservation } from '../../utils/getReservationActions';
import { SHELL_OVERLAY_Z_INDEX } from 'constants/zIndex';

const DATE_FORMAT = 'YYYY-MM-DD';

type DayStatus = 'assigned-to-me' | 'released' | 'in-use';

type ReleaseDay = {
  originalStatus: DayStatus;
  currentStatus: DayStatus;
  exclusionId?: string;
};

type ReleaseDeskModalProps = {
  open: boolean;
  reservation: ProfileReservation | null;
  recurrence: string | null;
  afterOpenChange?: (open: boolean) => void;
  onClose: () => void;
  onSuccess: () => void;
};

type Exclusions = GetProfileDeskExclusionsQuery['getReservationExclusions'];

const EMPTY_EXCLUSIONS: Exclusions = [];

const MomentCalendar =
  Calendar.generateCalendar<moment.Moment>(momentGenerateConfig);

const buildDaysForMonth = ({
  recurrence,
  exclusions,
  currentDate,
  timezone,
  today,
  threeMonthsFromToday,
}: {
  recurrence: string | null;
  exclusions: Exclusions;
  currentDate: moment.Moment;
  timezone: string;
  today: moment.Moment;
  threeMonthsFromToday: moment.Moment;
}) => {
  const days = new Map<string, ReleaseDay>();
  const monthStart = currentDate.clone().startOf('month');
  const monthEnd = currentDate.clone().endOf('month');

  for (
    const day = monthStart.clone();
    day.isSameOrBefore(monthEnd, 'day');
    day.add(1, 'day')
  ) {
    if (day.isBetween(today, threeMonthsFromToday, 'day', '[]')) {
      days.set(day.format(DATE_FORMAT), {
        originalStatus: 'in-use',
        currentStatus: 'in-use',
      });
    }
  }

  if (recurrence) {
    const rruleString = recurrence.replace('RRULE:', '');
    const options = RRule.parseString(rruleString);
    options.dtstart = new Date(
      Date.UTC(currentDate.year(), currentDate.month(), 1, 0, 0, 0)
    );
    const rule = new RRule(options);
    const scheduledDates = rule.between(
      moment.utc(monthStart).toDate(),
      moment.utc(monthEnd).toDate(),
      true
    );

    scheduledDates.forEach((date) => {
      const dateKey = moment.utc(date).endOf('day').format(DATE_FORMAT);
      const localDay = moment.tz(`${dateKey}T00:00:00`, timezone);
      if (localDay.isBetween(today, threeMonthsFromToday, 'day', '[]')) {
        days.set(dateKey, {
          originalStatus: 'assigned-to-me',
          currentStatus: 'assigned-to-me',
        });
      }
    });
  }

  exclusions.forEach((exclusion) => {
    const localDay = moment.tz(exclusion.startTime, timezone).startOf('day');
    if (
      localDay.isSame(currentDate, 'month') &&
      localDay.isBetween(today, threeMonthsFromToday, 'day', '[]')
    ) {
      days.set(localDay.format(DATE_FORMAT), {
        originalStatus: 'released',
        currentStatus: 'released',
        exclusionId: exclusion.id,
      });
    }
  });

  return days;
};

export const ReleaseDeskModal = ({
  open,
  reservation,
  recurrence,
  afterOpenChange,
  onClose,
  onSuccess,
}: ReleaseDeskModalProps): JSX.Element | null => {
  const { t } = useTranslation('peopleProfileSidebar', {
    keyPrefix: 'bookings.releaseDeskModal',
  });
  const timezone =
    reservation?.seat.location.timezone ?? reservation?.timeZone ?? 'UTC';
  const seriesId = reservation?.seriesId;
  const [currentDate, setCurrentDate] = useState(() =>
    moment.tz(timezone).startOf('month')
  );
  const [days, setDays] = useState<Map<string, ReleaseDay>>(new Map());

  useEffect(() => {
    if (open) {
      setCurrentDate(moment.tz(timezone).startOf('month'));
    }
  }, [open, timezone]);

  const today = useMemo(() => moment.tz(timezone).startOf('day'), [timezone]);
  const threeMonthsFromToday = useMemo(
    () => today.clone().add(3, 'months').endOf('day'),
    [today]
  );

  const { data, loading } = useGetProfileDeskExclusionsQuery({
    skip: !open || !seriesId,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      reservationSeriesId: seriesId ?? '',
      startDate: today.clone().utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
      endDate: threeMonthsFromToday
        .clone()
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss[Z]'),
    },
  });
  const exclusions = useMemo(
    () => data?.getReservationExclusions ?? EMPTY_EXCLUSIONS,
    [data?.getReservationExclusions]
  );

  useEffect(() => {
    if (!open || loading) {
      return;
    }

    setDays(
      buildDaysForMonth({
        recurrence,
        exclusions,
        currentDate,
        timezone,
        today,
        threeMonthsFromToday,
      })
    );
  }, [
    currentDate,
    exclusions,
    loading,
    open,
    recurrence,
    threeMonthsFromToday,
    timezone,
    today,
  ]);

  const [patchExclusions, { loading: saving }] =
    usePatchProfileDeskExclusionsMutation({
      refetchQueries: [
        'GetProfileBookings',
        'GetProfileDeskSchedules',
        'GetProfileDeskExclusions',
      ],
      awaitRefetchQueries: true,
    });

  const hasChanges = useMemo(
    () =>
      Array.from(days.values()).some(
        (day) => day.currentStatus !== day.originalStatus
      ),
    [days]
  );

  const toggleDay = (date: string) => {
    setDays((currentDays) => {
      const day = currentDays.get(date);
      if (!day || day.currentStatus === 'in-use') {
        return currentDays;
      }

      const nextDays = new Map(currentDays);
      nextDays.set(date, {
        ...day,
        currentStatus:
          day.currentStatus === 'assigned-to-me'
            ? 'released'
            : 'assigned-to-me',
      });
      return nextDays;
    });
  };

  const resetChanges = () => {
    setDays((currentDays) => {
      const nextDays = new Map<string, ReleaseDay>();
      currentDays.forEach((day, date) => {
        nextDays.set(date, {
          ...day,
          currentStatus: day.originalStatus,
        });
      });
      return nextDays;
    });
  };

  const cellRender: CalendarProps<moment.Moment>['cellRender'] = (
    current,
    info
  ) => {
    if (info.type !== 'date') {
      return null;
    }

    if (!current.isSame(currentDate, 'month')) {
      return <HiddenDateCell />;
    }

    const dateStr = current.format(DATE_FORMAT);
    const day = days.get(dateStr);

    let CellContentComponent = CellContentDefault;

    switch (day?.currentStatus) {
      case 'assigned-to-me':
        CellContentComponent = CellContentAssigned;
        break;
      case 'released':
        CellContentComponent = CellContentReleased;
        break;
      case 'in-use':
        CellContentComponent = CellContentDisabled;
        break;
      case undefined:
        CellContentComponent = CellContentDefault;
        break;
      default:
        CellContentComponent = CellContentDefault;
    }

    return (
      <CellWrapper>
        <CellContentComponent>
          <CellButton
            $status={day?.currentStatus || 'default'}
            type="button"
            disabled={!day || day.currentStatus === 'in-use'}
            onClick={() => {
              if (day && day.currentStatus !== 'in-use') {
                toggleDay(dateStr);
              }
            }}
          >
            {current.date()}
          </CellButton>
        </CellContentComponent>
      </CellWrapper>
    );
  };

  const headerRender = ({
    value,
    onChange,
  }: {
    value: moment.Moment;
    onChange: (date: moment.Moment) => void;
  }) => {
    const current = value.clone();

    const prevMonth = () => {
      const newValue = current.clone().subtract(1, 'months');
      onChange(newValue);
      setCurrentDate(newValue);
    };

    const nextMonth = () => {
      const newValue = current.clone().add(1, 'months');
      onChange(newValue);
      setCurrentDate(newValue);
    };

    return (
      <CalendarHeader>
        <HeaderIconButton
          type="button"
          aria-label={t('previousMonth')}
          onClick={prevMonth}
        >
          <ChevronLeftOutline />
        </HeaderIconButton>
        <HeaderTitle>{current.format('MMMM YYYY')}</HeaderTitle>
        <HeaderIconButton
          type="button"
          aria-label={t('nextMonth')}
          onClick={nextMonth}
        >
          <ChevronRightOutline />
        </HeaderIconButton>
      </CalendarHeader>
    );
  };

  const handleSave = async () => {
    if (!seriesId) {
      return;
    }

    const modifiedDays = Array.from(days.entries()).filter(
      ([, day]) => day.currentStatus !== day.originalStatus
    );
    const dates = modifiedDays
      .filter(([, day]) => day.currentStatus === 'released')
      .map(([date]) => ({
        start: {
          dateTime: moment.tz(`${date}T00:00:00`, timezone).format(),
          timeZone: timezone,
        },
        end: {
          dateTime: moment.tz(`${date}T23:59:59`, timezone).format(),
          timeZone: timezone,
        },
      }));
    const exclusionIds = modifiedDays
      .filter(([, day]) => day.currentStatus === 'assigned-to-me')
      .map(([, day]) => day.exclusionId)
      .filter((id): id is string => Boolean(id));

    try {
      await patchExclusions({
        variables: {
          seriesId,
          dates,
          exclusionIds,
          hasDates: dates.length > 0,
          hasDeletions: exclusionIds.length > 0,
        },
      });
      void message.success(t('success'));
      onSuccess();
    } catch {
      void message.error(t('failure'));
    }
  };

  if (!reservation) {
    return null;
  }

  return (
    <Modal
      open={open}
      zIndex={SHELL_OVERLAY_Z_INDEX}
      title={t('title')}
      afterOpenChange={afterOpenChange}
      onCancel={onClose}
      footer={null}
      width={575}
      modalRender={(modalNode) => (
        <ModalEventBoundary onClick={(event) => event.stopPropagation()}>
          {modalNode}
        </ModalEventBoundary>
      )}
    >
      <div>{t('instruction')}</div>
      {loading ? (
        <CalendarLoadingContainer data-testid="skeleton">
          <Skeleton />
          <Skeleton />
        </CalendarLoadingContainer>
      ) : (
        <CalendarContainer>
          <MomentCalendar
            fullscreen={false}
            fullCellRender={cellRender}
            onPanelChange={(value) => {
              setCurrentDate(value);
            }}
            headerRender={headerRender}
          />
        </CalendarContainer>
      )}
      <Legend>
        <LegendItem>
          <LegendSwatch $status="assigned-to-me" />
          <Typography.Text>{t('assignedToMe')}</Typography.Text>
        </LegendItem>
        <LegendItem>
          <LegendSwatch $status="released" />
          <Typography.Text>{t('released')}</Typography.Text>
        </LegendItem>
        <LegendItem>
          <LegendSwatch $status="in-use" />
          <Typography.Text>{t('releasedAndBooked')}</Typography.Text>
        </LegendItem>
      </Legend>
      <Footer>
        <ButtonsContainer>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            loading={saving}
          >
            {t('save')}
          </Button>
          <Button
            type="default"
            onClick={resetChanges}
            disabled={!hasChanges || saving}
          >
            {t('discardChanges')}
          </Button>
          <Button type="text" onClick={onClose} disabled={saving}>
            {t('close')}
          </Button>
        </ButtonsContainer>
      </Footer>
    </Modal>
  );
};

const CellWrapper = styled.div`
  display: flex;
  padding: 2px 6px;
  justify-content: center;
  align-items: flex-start;
`;

const HiddenDateCell = styled.div`
  visibility: hidden;
`;

const ModalEventBoundary = styled.div``;

const CalendarHeader = styled.div`
  display: flex;
  padding: 8px;
  align-items: center;
  justify-content: space-between;
`;

const HeaderIconButton = styled.button`
  appearance: none;
  display: flex;
  width: 24px;
  height: 24px;
  padding: 0;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: inherit;
`;

const HeaderTitle = styled.span`
  font-size: 16px;
`;

const sharedCellContentStyles = `
  display: flex;
  width: 28px;
  height: var(--Components-DatePicker-Component-cellHeight, 28px);
  min-width: var(--Components-DatePicker-Component-cellHeight, 28px);
  padding: 0px 3px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: var(--Components-DatePicker-Global-borderRadius, 4px);
`;

const CellContentAssigned = styled.div`
  ${sharedCellContentStyles}
  background: var(--Components-DatePicker-Global-colorPrimary, #2774c1);
`;

const CellContentReleased = styled.div`
  ${sharedCellContentStyles}
  border: var(--Components-DatePicker-Global-lineWidth, 1px) solid
    var(--Components-DatePicker-Component-activeBorderColor, #2774c1);
`;

const CellContentDisabled = styled.div`
  ${sharedCellContentStyles}
  background: #f5f5f5;
`;

const CellContentDefault = styled.div`
  ${sharedCellContentStyles}
  background: transparent;
`;

const CellButton = styled.button<{ $status: string }>`
  appearance: none;
  width: 24px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${(props) =>
    props.$status === 'assigned-to-me'
      ? 'var(--Components-DatePicker-Global-colorTextLightSolid, #fff)'
      : '#000000F2'};
  text-align: center;
  line-height: var(--Components-DatePicker-Global-lineHeight, 22px);
  cursor: ${(props) =>
    props.$status === 'assigned-to-me' || props.$status === 'released'
      ? 'pointer'
      : 'default'};

  &:disabled {
    cursor: ${(props) =>
      props.$status === 'in-use' ? 'not-allowed' : 'default'};
  }
`;

const Legend = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--Space-Margin-marginXS, 8px);
  align-self: stretch;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  gap: var(--Space-Margin-marginXS, 8px);
`;

const LegendSwatch = styled.span<{ $status: DayStatus }>`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  border-radius: 100px;
  background: ${({ $status }) =>
    $status === 'assigned-to-me' ? '#2774C1' : '#FFF'};
  border: 1px solid
    ${({ $status }) =>
      $status === 'assigned-to-me'
        ? 'transparent'
        : $status === 'in-use'
        ? '#D9D9D9'
        : '#2774C1'};
`;

const CalendarLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
`;

const CalendarContainer = styled.div`
  padding: 16px 0;
`;

const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--Space-Margin-marginSM, 12px);
  background: var(--Colors-Neutral-colorWhite, #fff);
  padding-top: var(--Space-Padding-padding, 24px);
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--Space-Margin-marginSM, 12px);
  margin-left: auto;
`;
